import ExpandMore from "@mui/icons-material/ExpandMore";
import { Button, Divider, Menu, MenuList } from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { Row } from "../ChemicalFacilitiesDataGrid";
import { SetDefaultExecutionPlanAuto } from "./SetDefaultExecutionPlanAuto";
import { SetDefaultExecutionPlanManual } from "./SetDefaultExecutionPlanManual";
import {
  VerifyAndSubmitDialog,
  VerifyAndSubmitMenuItem,
} from "./VerifyAndSubmit";
import { VerifyCredentialsMenuItem } from "./VerifyCredentials";
import { ResetReportsMenuItem, ResetReportsModal } from "./ResetReports";
import {
  AddFulfillmentNoteMenuItem,
  AddFulfillmentNoteDialog_ as AddFulfillmentNoteDialog,
} from "./AddFulfillmentNote";
import { OpenRevisionMenuItem, OpenRevisionDialogs } from "./OpenRevision";
import { brandColors } from "../../../../../util";

const buttonId = "bulk-actions-button";
const menuId = "bulk-actions-menu";

type BulkActionMenuProps = {
  selectedRows: Row[];
  reportingYear: number;
};

type DialogState =
  | "verify&submit"
  | "resetReports"
  | "addFulfillmentNote"
  | "openRevisions"
  | null;

const bulkActionContext = createContext<{
  selectedRows: Row[];
  reportingYear: number;
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
}>({
  selectedRows: [],
  reportingYear: 0,
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
});

export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

const ChemicalFacilitiesActionsMenu = ({
  selectedRows,
  reportingYear,
}: BulkActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
    setDialogState(null);
  };

  return (
    <bulkActionContext.Provider
      value={{
        selectedRows,
        reportingYear,
        onClose,
        dialogState,
        setDialogState,
      }}
    >
      <Button
        id={buttonId}
        size="medium"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={!selectedRows.length}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        sx={{
          whiteSpace: "nowrap",
          backgroundColor: brandColors["encamp-orange"][500], // #F08A0D
          color: "white",
          "&:hover": {
            backgroundColor: brandColors["encamp-orange"][700], // #C35202
          },
        }}
      >
        Bulk Actions
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <OpenRevisionMenuItem />
          <ResetReportsMenuItem />
          <VerifyAndSubmitMenuItem />
          <VerifyCredentialsMenuItem />
          <Divider />
          <SetDefaultExecutionPlanManual />
          <SetDefaultExecutionPlanAuto />
          <Divider />
          <AddFulfillmentNoteMenuItem />
        </MenuList>
      </Menu>
      <VerifyAndSubmitDialog />
      <ResetReportsModal />
      <AddFulfillmentNoteDialog />
      <OpenRevisionDialogs />
    </bulkActionContext.Provider>
  );
};

export { ChemicalFacilitiesActionsMenu };
